import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getConceptsValuesSearch,
  type ConceptValue,
} from "../services/regulationsService";

function mapConceptSearchItemsToValues(
  items: Array<{ code: string; name: string; description?: string }>,
): ConceptValue[] {
  return items.map((item) => ({
    id: item.code,
    code: item.code,
    name: item.name ?? item.code,
    description: item.description ?? "",
  }));
}

function isConceptSearchResponseOk(
  response: Awaited<ReturnType<typeof getConceptsValuesSearch>>,
): boolean {
  return (
    response.status === "success" ||
    response.code === 200 ||
    response.code === 201 ||
    Array.isArray(response.data)
  );
}

export type LoadConceptValuesOptions = {
  /** Bypass the in-memory "same query" skip so clear / reopen can refetch. */
  force?: boolean;
};

export interface UsePaginatedConceptValuesResult {
  input: string;
  setInput: (value: string) => void;
  options: ConceptValue[];
  loading: boolean;
  hasMoreNext: boolean;
  loadingMore: "next" | "prev" | null;
  loadOptions: (
    searchName?: string,
    loadOpts?: LoadConceptValuesOptions,
  ) => Promise<void>;
  loadNext: () => Promise<void>;
  loadPrev: () => Promise<void>;
}

const DEFAULT_PAGE_SIZE = 20;

export type UsePaginatedConceptValuesOptions = {
  /**
   * When true and conceptCode is empty, search across all concept names
   * in `allConceptCodes` (sent as required `concept_names`).
   */
  allowEmptyConceptAsAll?: boolean;
  /**
   * Concept names to pass as `concept_names` when conceptCode is empty ("All").
   */
  allConceptCodes?: string[];
  /**
   * When true with allowEmptyConceptAsAll, wait until allConceptCodes is
   * non-empty before searching. Prevents a redundant "search all" request
   * while concept options are still loading.
   */
  waitForAllConceptCodes?: boolean;
};

function normalizeConceptNames(names: readonly string[]): string[] {
  return names.map((name) => name.trim()).filter(Boolean);
}

export function dedupeConceptValues(items: ConceptValue[]): ConceptValue[] {
  const seen = new Set<string>();
  const unique: ConceptValue[] = [];
  for (const item of items) {
    const key = item.code.trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(item);
  }
  return unique;
}

/**
 * Loads concept values via user-search.
 * `conceptCode` is sent as required `concept_names` (comma-separated when
 * searching across multiple dimensions).
 */
export function usePaginatedConceptValues(
  conceptCode: string,
  pageSize: number = DEFAULT_PAGE_SIZE,
  options?: UsePaginatedConceptValuesOptions,
): UsePaginatedConceptValuesResult {
  const allowEmptyConceptAsAll = options?.allowEmptyConceptAsAll === true;
  const waitForAllConceptCodes = options?.waitForAllConceptCodes === true;
  const allConceptCodesKey = options?.allConceptCodes?.join(",") ?? "";

  const conceptNames = useMemo(() => {
    const trimmed = conceptCode.trim();
    if (trimmed) return [trimmed];
    if (allowEmptyConceptAsAll && allConceptCodesKey) {
      return normalizeConceptNames(allConceptCodesKey.split(","));
    }
    return [];
  }, [allowEmptyConceptAsAll, allConceptCodesKey, conceptCode]);

  const waitingForAllCodes =
    waitForAllConceptCodes &&
    allowEmptyConceptAsAll &&
    !conceptCode.trim() &&
    conceptNames.length === 0;

  const canSearch = !waitingForAllCodes && conceptNames.length > 0;
  const conceptNamesKey = conceptNames.join(",");
  const conceptNamesRef = useRef(conceptNames);
  conceptNamesRef.current = conceptNames;

  const [input, setInputState] = useState("");
  const inputRef = useRef(input);
  inputRef.current = input;

  const setInput = useCallback((value: string) => {
    inputRef.current = value;
    setInputState(value);
  }, []);

  const [optionsState, setOptions] = useState<ConceptValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMoreNext, setHasMoreNext] = useState(true);
  const [loadingMore, setLoadingMore] = useState<"next" | "prev" | null>(null);
  const loadOptionsRequestIdRef = useRef(0);
  const loadMoreGenerationRef = useRef(0);
  const lastLoadKeyRef = useRef("");
  const pageRef = useRef(page);
  pageRef.current = page;
  const hasMoreNextRef = useRef(hasMoreNext);
  hasMoreNextRef.current = hasMoreNext;
  const loadingMoreRef = useRef(loadingMore);
  loadingMoreRef.current = loadingMore;

  const loadOptions = useCallback(
    async (searchName?: string, loadOpts?: LoadConceptValuesOptions) => {
      const names = normalizeConceptNames(conceptNamesRef.current);
      if (names.length === 0) {
        lastLoadKeyRef.current = "";
        setOptions([]);
        setHasMoreNext(false);
        hasMoreNextRef.current = false;
        return;
      }

      const trimmedSearch = searchName?.trim() || undefined;
      const loadKey = `${names.join(",")}|${trimmedSearch ?? ""}|${pageSize}`;
      if (!loadOpts?.force && lastLoadKeyRef.current === loadKey) {
        return;
      }
      lastLoadKeyRef.current = loadKey;

      // Keep loadNext/loadPrev on the same `name` filter as this page-1 load.
      inputRef.current = trimmedSearch ?? "";
      setInputState(trimmedSearch ?? "");

      const requestId = ++loadOptionsRequestIdRef.current;
      // Invalidate in-flight loadNext/loadPrev and clear sticky loadingMore so
      // scroll-to-load-more works again after a search/reload.
      loadMoreGenerationRef.current += 1;
      loadingMoreRef.current = null;
      setLoadingMore(null);
      try {
        setLoading(true);
        // Drop stale options immediately so consumers can show a loading state.
        setOptions([]);
        pageRef.current = 1;
        setPage(1);
        hasMoreNextRef.current = true;
        setHasMoreNext(true);
        const response = await getConceptsValuesSearch(
          names,
          trimmedSearch,
          pageSize,
          1,
        );
        if (requestId !== loadOptionsRequestIdRef.current) return;
        if (isConceptSearchResponseOk(response)) {
          const data = dedupeConceptValues(
            mapConceptSearchItemsToValues(
              Array.isArray(response.data) ? response.data : [],
            ),
          );
          setOptions(data);
          const more = data.length >= pageSize;
          hasMoreNextRef.current = more;
          setHasMoreNext(more);
        } else {
          lastLoadKeyRef.current = "";
          setOptions([]);
          hasMoreNextRef.current = false;
          setHasMoreNext(false);
        }
      } catch (error) {
        if (requestId !== loadOptionsRequestIdRef.current) return;
        lastLoadKeyRef.current = "";
        console.error(
          `Error loading concept values for ${conceptCode || "all"}:`,
          error,
        );
        setOptions([]);
        hasMoreNextRef.current = false;
        setHasMoreNext(false);
      } finally {
        if (requestId === loadOptionsRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [conceptCode, pageSize],
  );

  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;

  const loadNext = useCallback(async () => {
    const names = normalizeConceptNames(conceptNamesRef.current);
    if (names.length === 0 || !hasMoreNextRef.current || loadingMoreRef.current) {
      return;
    }
    const nextPage = pageRef.current + 1;
    const searchName = inputRef.current.trim() || undefined;
    const generation = ++loadMoreGenerationRef.current;
    loadingMoreRef.current = "next";
    try {
      setLoadingMore("next");
      const response = await getConceptsValuesSearch(
        names,
        searchName,
        pageSize,
        nextPage,
      );
      if (generation !== loadMoreGenerationRef.current) return;
      if (isConceptSearchResponseOk(response)) {
        const newData = dedupeConceptValues(
          mapConceptSearchItemsToValues(
            Array.isArray(response.data) ? response.data : [],
          ),
        );
        setOptions((prev) => {
          const existingCodes = new Set(
            prev.map((item) => item.code.trim().toLowerCase()).filter(Boolean),
          );
          const appended = newData.filter(
            (item) => !existingCodes.has(item.code.trim().toLowerCase()),
          );
          return appended.length > 0 ? [...prev, ...appended] : prev;
        });
        pageRef.current = nextPage;
        setPage(nextPage);
        const more = newData.length >= pageSize;
        hasMoreNextRef.current = more;
        setHasMoreNext(more);
        // Allow a later unfiltered loadOptions() to replace the stacked pages.
        lastLoadKeyRef.current = "";
      } else {
        hasMoreNextRef.current = false;
        setHasMoreNext(false);
      }
    } catch {
      if (generation === loadMoreGenerationRef.current) {
        hasMoreNextRef.current = false;
        setHasMoreNext(false);
      }
    } finally {
      if (generation === loadMoreGenerationRef.current) {
        loadingMoreRef.current = null;
        setLoadingMore(null);
      }
    }
  }, [pageSize]);

  const loadPrev = useCallback(async () => {
    const names = normalizeConceptNames(conceptNamesRef.current);
    if (names.length === 0 || pageRef.current <= 1 || loadingMoreRef.current) {
      return;
    }
    const prevPage = pageRef.current - 1;
    const searchName = inputRef.current.trim() || undefined;
    const generation = ++loadMoreGenerationRef.current;
    loadingMoreRef.current = "prev";
    try {
      setLoadingMore("prev");
      const response = await getConceptsValuesSearch(
        names,
        searchName,
        pageSize,
        prevPage,
      );
      if (generation !== loadMoreGenerationRef.current) return;
      if (isConceptSearchResponseOk(response)) {
        const newData = dedupeConceptValues(
          mapConceptSearchItemsToValues(
            Array.isArray(response.data) ? response.data : [],
          ),
        );
        setOptions((prev) => {
          const existingCodes = new Set(prev.map((item) => item.code));
          const toPrepend = newData.filter((item) => !existingCodes.has(item.code));
          return toPrepend.length > 0 ? [...toPrepend, ...prev] : prev;
        });
        pageRef.current = prevPage;
        setPage(prevPage);
        lastLoadKeyRef.current = "";
      }
    } catch {
      // keep current page
    } finally {
      if (generation === loadMoreGenerationRef.current) {
        loadingMoreRef.current = null;
        setLoadingMore(null);
      }
    }
  }, [pageSize]);

  // Auto-load when the concept scope changes. Keep `loadOptions` out of deps
  // so identity churn does not re-fire the same query.
  useEffect(() => {
    setInput("");
    if (!canSearch) {
      lastLoadKeyRef.current = "";
      setOptions([]);
      setPage(1);
      setHasMoreNext(false);
      return;
    }
    void loadOptionsRef.current();
  }, [canSearch, conceptNamesKey, setInput]);

  return {
    input,
    setInput,
    options: optionsState,
    loading,
    hasMoreNext,
    loadingMore,
    loadOptions,
    loadNext,
    loadPrev,
  };
}
